import {createSlice, type PayloadAction} from '@reduxjs/toolkit';
import type {ColorPaletteInfo, ColorPalettePill} from '@Libraries/color-palettes.library';
import type {RootState} from '@/store';

interface ColorPalettesReduxState {
  adminColorPalettes: ColorPaletteInfo[] | undefined;
  brandKitColorPalettes: ColorPaletteInfo[] | undefined;
  colorPaletteBeingApplied: ColorPaletteInfo | undefined;
  selectedColorPalette: ColorPaletteInfo | undefined;
  recentPalettes: ColorPaletteInfo[] | undefined;
  filterPills: ColorPalettePill[] | undefined;
}

const initialState: ColorPalettesReduxState = {
  adminColorPalettes: undefined,
  brandKitColorPalettes: undefined,
  colorPaletteBeingApplied: undefined,
  selectedColorPalette: undefined,
  recentPalettes: undefined,
  filterPills: undefined,
};

const colorPalettesSlice = createSlice({
  name: 'ColorPalettes',
  initialState,
  reducers: {
    setAdminColorPalettes: (state, action: PayloadAction<ColorPaletteInfo[]>) => {
      state.adminColorPalettes = action.payload;
    },

    setBrandKitColorPalettes: (state, action: PayloadAction<ColorPaletteInfo[] | undefined>) => {
      if (action.payload === undefined) {
        state.brandKitColorPalettes = undefined;
        return;
      }

      state.brandKitColorPalettes = action.payload;
    },

    setColorPaletteBeingApplied: (state, action: PayloadAction<ColorPaletteInfo | undefined>) => {
      state.colorPaletteBeingApplied = action.payload;
    },

    setSelectedColorPalette: (state, action: PayloadAction<ColorPaletteInfo | undefined>) => {
      state.selectedColorPalette = action.payload;
    },

    setFilterPills: (state, action: PayloadAction<ColorPalettePill[]>) => {
      state.filterPills = action.payload;
    },

    setRecentPalettes: (state, action: PayloadAction<ColorPaletteInfo[]>) => {
      state.recentPalettes = action.payload;
    },
  },
});

export const {setAdminColorPalettes, setBrandKitColorPalettes, setColorPaletteBeingApplied, setSelectedColorPalette, setFilterPills, setRecentPalettes} =
  colorPalettesSlice.actions;

export const colorPalettesReducer = colorPalettesSlice.reducer;

/** Whether a color palette is currently being applied (set during the auto-branding apply flow). */
export const selectIsColorPaletteBeingApplied = (state: RootState): boolean => {
  return state.colorPalettes.colorPaletteBeingApplied !== undefined;
};
